import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fetchTeamDetail, TeamDetailResponse } from "@/services/adminCollegeTeams";
import { ArrowLeft } from "lucide-react";

const AdminCollegeTeamDetail = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<TeamDetailResponse | null>(null);
  const season = "2025_fall";

  const loadTeamDetail = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      const response = await fetchTeamDetail(teamId, season);
      setData(response);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Failed to load team details", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [teamId, season, toast]);

  useEffect(() => {
    loadTeamDetail();
  }, [loadTeamDetail]);

  const formatGender = (g: string | null) => {
    if (!g) return "-";
    return g === "male" ? "Men" : g === "female" ? "Women" : g;
  };

  const formatRank = (rank: number | null) => {
    if (rank === null || rank === undefined || rank === 0) return "-";
    return `#${rank}`;
  };

  const formatNumber = (n: number | null, decimals = 2) => {
    if (n === null || n === undefined) return "-";
    return n.toFixed(decimals);
  };

  if (loading) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="py-8 text-center text-muted-foreground">Loading team details...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="py-8 text-center text-muted-foreground">Team not found</div>
        <Button variant="outline" onClick={() => navigate("/recruit-admin/college-teams")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Teams
        </Button>
      </div>
    );
  }

  const { team, college, ranking, students } = data;

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/recruit-admin/college-teams")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{college.name}</h1>
          <p className="text-muted-foreground">
            {team.name} - {formatGender(team.gender)}
          </p>
        </div>
      </div>

      {/* Team Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Team Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Team Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Division</span>
              <span>{team.division || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Conference</span>
              <span>{college.conference || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">State</span>
              <span>{college.state || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Region</span>
              <span>{team.region || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Head Coach</span>
              <span>{team.head_coach || "-"}</span>
            </div>
            {team.head_coach_email && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Coach Email</span>
                <a
                  href={`mailto:${team.head_coach_email}`}
                  className="text-blue-600 hover:underline"
                >
                  {team.head_coach_email}
                </a>
              </div>
            )}
            {team.team_url && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Team URL</span>
                <a
                  href={team.team_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline truncate max-w-[150px]"
                >
                  View
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ranking Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Rankings (Fall 2025)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {ranking ? (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">National Rank</span>
                  <span className="font-semibold">{formatRank(ranking.national_rank)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scoring Avg</span>
                  <span>{formatNumber(ranking.scoring_avg_adj)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Roster Size</span>
                  <span>{ranking.roster_size ?? "-"}</span>
                </div>
                {ranking.roster_scoring_range && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Score Range</span>
                      <span>
                        {formatNumber(ranking.roster_scoring_range.min)} -{" "}
                        {formatNumber(ranking.roster_scoring_range.max)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Median (P50)</span>
                      <span>{formatNumber(ranking.roster_scoring_range.p50)}</span>
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">No ranking data available</p>
            )}
          </CardContent>
        </Card>

        {/* College Aliases */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">College Aliases</CardTitle>
            <CardDescription>Alternative names for searching</CardDescription>
          </CardHeader>
          <CardContent>
            {college.aliases && college.aliases.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {college.aliases.map((alias, i) => (
                  <Badge key={i} variant="secondary">
                    {alias}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No aliases configured</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Students Table */}
      <Card>
        <CardHeader>
          <CardTitle>Students ({students.length})</CardTitle>
          <CardDescription>
            {data.active_count} active on roster, {students.length - data.active_count}{" "}
            inactive/former
          </CardDescription>
        </CardHeader>
        <CardContent>
          {students.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No students found for this team
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Class Year</TableHead>
                    <TableHead>Grad Year</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead className="text-right">JGS Rank</TableHead>
                    <TableHead className="text-right">JGS Diff</TableHead>
                    <TableHead className="text-right">College Avg</TableHead>
                    <TableHead className="text-right">College Rank</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map(student => (
                    <TableRow key={student.student_id}>
                      <TableCell className="font-medium">{student.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={student.is_active ? "default" : "secondary"}>
                          {student.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatGender(student.gender)}</TableCell>
                      <TableCell>{student.class_year || "-"}</TableCell>
                      <TableCell>{student.graduation_year || "-"}</TableCell>
                      <TableCell>{student.state || "-"}</TableCell>
                      <TableCell className="text-right">{formatRank(student.jgs_rank)}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(student.jgs_scoring_differential)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(student.college_scoring_avg)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatRank(student.college_national_rank)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCollegeTeamDetail;
